<template>
  <slot name="toggle" :toggle-binds="toggleBinds" />
  <slot name="dialog" :dialog-binds="dialogBinds" :backdrop-binds="backdropBinds" />
</template>

<script setup>
import { ref, watch, toRef, reactive, onUnmounted } from 'vue'
import { uniqueId } from 'gleam-shared/helpers/nodash'
import { trapFocus, releaseFocus } from 'gleam-shared/browser/focustrap'

const emit = defineEmits(['close'])
const props = defineProps({
  open: {
    type: Boolean,
    required: true
  }
})

const marker = uniqueId('HelpCenterDropdown')
const openRef = toRef(props, 'open')
const toggleRef = ref(null)
const dialogRef = ref(null)
const dialogBinds = reactive({ ref: onDialogRef, open: openRef })
const toggleBinds = { ref: onToggleRef }
const backdropBinds = { onClick: emitClose }

onUnmounted(revertFocus)
watch([openRef, toggleRef, dialogRef], toggleFocus)
watch(openRef, manageListeners)

function onToggleRef(value) {
  toggleRef.value = value
}

function onDialogRef(value) {
  dialogRef.value = value
}

function onKeydown(event) {
  if (event.key === 'Escape') {
    emitClose()
  }
}

function onClick(event) {
  if (!openRef.value || !toggleRef.value || !dialogRef.value) {
    return
  }

  const crossedPaths = isOnPath(toggleRef.value, event) || isOnPath(dialogRef.value, event)

  if (!crossedPaths) {
    emitClose()
  }
}

function emitClose() {
  emit('close')
}

function revertFocus() {
  releaseFocus(marker)
}

function toggleFocus([open, toggle, dialog]) {
  if (open && toggle && dialog) {
    trapFocus(marker, [toggle, dialog])
  } else {
    releaseFocus(marker)
  }
}

function manageListeners(open) {
  if (open) {
    window.addEventListener('keydown', onKeydown, { passive: true })
    document.addEventListener('click', onClick, { passive: true })
  } else {
    window.removeEventListener('keydown', onKeydown)
    document.removeEventListener('click', onClick)
  }
}

function isOnPath(el, event) {
  for (const step of event.composedPath()) {
    if (step === el) {
      return true
    }
  }

  return false
}
</script>
